import { SlateElementType } from '@vedalib/rich-text'
import React, { useCallback, useEffect } from 'react'

import {
  getSelectedFormat,
  getSetToolbarForm,
  getUpdateFormat,
  useRichTextContext,
} from 'components/form/RichText/useRichTextContext'
import { IconButton } from 'components/uiKit/Button'
import Divider from 'components/uiKit/Divider'
import Input from 'components/uiKit/Input'
import { KitSize } from 'components/uiKit/KitTypes'
import Switcher from 'components/uiKit/Switcher'
import { Typography } from 'components/uiKit/Typography'
import { stopPropagation } from 'constants/commonConstans'
import { t } from 'services/Translation'
import { validateLinkUrl } from 'utils/websiteValidation'

import * as s from './LinkForm.module.scss'

const LinkForm: React.FC = () => {
  const selectedFormat = useRichTextContext(getSelectedFormat)
  const { url = '', target = '_blank' } = selectedFormat?.inline || {}
  const [localUrl, setLocalUrl] = React.useState<string>(url)
  const [localTarget, setLocalTarget] = React.useState<'_self' | '_blank'>(target)
  const onUpdateFormat = useRichTextContext(getUpdateFormat)
  const setToolbarForm = useRichTextContext(getSetToolbarForm)
  const handleClose = useCallback(() => setToolbarForm('default'), [setToolbarForm])
  const isValid = validateLinkUrl(localUrl)

  const isEditing = Boolean(url)

  const onSave = () => {
    onUpdateFormat(SlateElementType.link, { url: localUrl, target: localTarget })
    handleClose()
  }

  const onDelete = () => {
    onUpdateFormat(SlateElementType.link, null)
    handleClose()
  }

  const handleChangeTarget = () => {
    const newTarget = localTarget === '_self' ? '_blank' : '_self'
    setLocalTarget(newTarget)
    if (url) {
      onUpdateFormat(SlateElementType.link, { url: localUrl, target: newTarget })
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.stopPropagation()
      e.preventDefault()
      onSave()
    }
    if (e.key === 'Escape') {
      e.stopPropagation()
      e.preventDefault()
      handleClose()
    }
  }

  useEffect(() => {
    setLocalUrl(url)
  }, [url])

  useEffect(() => {
    setLocalTarget(target)
  }, [target])

  // stopPropagation - HOTFIX for https://www.notion.so/2413-1885457f94c5812498f2e5ff0fc8f373
  return (
    <div className={s.root} onClick={stopPropagation} onKeyDown={onKeyDown}>
      <IconButton
        icon='builderInputBack'
        name='backButton'
        onClick={handleClose}
        size={KitSize.S}
        styleType='secondary'
      />
      <div className={s.input}>
        <Input
          autoFocus={!localUrl}
          error={!isValid}
          name='richTextLink'
          onChange={setLocalUrl}
          styleType='ghost'
          value={localUrl}
        />
      </div>
      <div className={s.controls}>
        {isEditing && (
          <IconButton
            icon='otherBrokenLink'
            name='delete'
            onClick={onDelete}
            size={KitSize.S}
            styleType='ghost'
          />
        )}
        {localUrl && <Divider size={KitSize.S} styleType='vertical' free />}
        <div className={s.newTab}>
          <Typography>{t('elements.richText.label.target')}</Typography>
          <Switcher name='target' onChange={handleChangeTarget} value={localTarget !== '_self'} />
        </div>
        <Divider size={KitSize.S} styleType='vertical' free />
        <IconButton
          icon={!localUrl ? 'iconSendDisabled' : 'iconSend'}
          name='create'
          onClick={onSave}
          size={KitSize.S}
          styleType='ghost'
        />
      </div>
    </div>
  )
}

export default LinkForm
